/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file MailOrganizeSheet.js
 * @description
 * Premium bottom sheet to ORGANIZE one or many messages in one go: move to a folder, toggle
 * labels and set / clear a colour mark. Works on every account. Nothing is written until the
 * user taps "Apply"; then a single `POST /mail/messages/organize` is sent with only the
 * dimensions that actually changed (see {@link module:helpers/mailOrganize.buildOrganizePayload}).
 *
 * Opened from the multi-select toolbar of `MailHomeScreen` and the kebab menu of
 * `MailThreadScreen`.
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Toast from 'react-native-toast-message';
import Icon from '../management/SafeIcon';
import {PremiumSheet} from '../settings/SettingsUiKit';
import {useManagementTheme} from '../../helpers/managementTheme';
import {useMail} from '../../helpers/MailContext';
import {organizeMailMessages} from '../../services/mailApi';
import {primeMailMessages} from '../../helpers/mailCache';
import {
    MAIL_COLOR_IDS, buildLabelIndex, buildOrganizePayload, colorHex, messageColorId,
} from '../../helpers/mailOrganize';

/** Stable message id across the `id` / `_id` shapes. */
const idOf = (message) => String(message?.id || message?._id || '');

/** Section heading inside the sheet. */
const SectionTitle = ({label}) => {
    const m = useManagementTheme();

    return <Text style={[styles.sectionTitle, {color: m.colors.muted}]}>{label}</Text>;
};

/**
 * Organize bottom sheet.
 * @param {Object} props
 * @param {boolean} props.visible Sheet visibility.
 * @param {() => void} props.onClose Dismiss handler.
 * @param {Object[]} props.messages Message documents being organized (selected rows / the open message).
 * @param {Array<{key:string,label:string,icon?:string,color?:string}>} props.folders Folder targets (standard + custom).
 * @param {(result:Object) => void} [props.onApplied] Called after a successful apply.
 * @returns {JSX.Element}
 */
const MailOrganizeSheet = ({visible, onClose, messages, folders, onApplied}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const mail = useMail();
    const list = useMemo(() => (Array.isArray(messages) ? messages : []), [messages]);
    const ids = useMemo(() => list.map(idOf).filter(Boolean), [list]);
    const labelIndex = useMemo(() => buildLabelIndex(mail.labels), [mail.labels]);
    const labelList = useMemo(() => Object.values(labelIndex), [labelIndex]);

    // Labels already on the selection (union): toggling one off removes it from every selected message.
    const initialLabels = useMemo(() => {
        const set = new Set();
        list.forEach((msg) => (Array.isArray(msg?.labels) ? msg.labels : []).forEach((l) => {
            const id = typeof l === 'object' ? String(l?.id || l?._id || '') : String(l);
            if (id) set.add(id);
        }));

        return set;
    }, [list]);
    const initialColor = useMemo(() => {
        const first = list.length ? messageColorId(list[0]) : '';

        return list.every((msg) => messageColorId(msg) === first) ? first : null;
    }, [list]);

    const [folder, setFolder] = useState(null);
    const [wanted, setWanted] = useState(new Set());
    const [color, setColor] = useState(undefined);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (visible) {
            setFolder(null);
            setWanted(new Set(initialLabels));
            setColor(undefined);
            setBusy(false);
        }
        // Snapshot once per opening: a background refresh must not wipe the user's choices.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const toggleLabel = useCallback((id) => {
        setWanted((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);

            return next;
        });
    }, []);

    const payload = useMemo(() => {
        const addLabelIds = [...wanted].filter((id) => !initialLabels.has(id));
        const removeLabelIds = [...initialLabels].filter((id) => !wanted.has(id));

        return buildOrganizePayload({ids, folder, addLabelIds, removeLabelIds, color});
    }, [ids, folder, wanted, initialLabels, color]);

    const apply = useCallback(async () => {
        if (!payload || busy) return;
        setBusy(true);
        try {
            const res = await organizeMailMessages(payload);
            if (Array.isArray(res?.items)) primeMailMessages(res.items);
            mail.invalidate();
            if (payload.folderId) mail.refreshFolders();
            Toast.show({type: 'success', text1: t('mail_organize_applied')});
            if (onApplied) onApplied(res);
            onClose();
        } catch (e) {
            const key = e?.msg === 'INVALID_COLOR' ? 'mail_organize_invalid_color' : null;
            Toast.show({type: 'error', text1: t('mail_organize_failed'), text2: key ? t(key) : e?.message});
        } finally {
            setBusy(false);
        }
    }, [payload, busy, mail, t, onApplied, onClose]);

    const shownColor = color === undefined ? initialColor : color;

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t('mail_organize_title')}
            subtitle={t('mail_organize_subtitle', {count: ids.length})}
            cancelText={t('mail_cancel')}
        >
            <SectionTitle label={t('mail_organize_folder')} />
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.rowWrap}>
                {(Array.isArray(folders) ? folders : []).map((f) => {
                    const active = folder === f.key;

                    return (
                        <TouchableOpacity
                            key={f.key}
                            onPress={() => setFolder(active ? null : f.key)}
                            activeOpacity={0.85}
                            style={[styles.chip, {
                                backgroundColor: active ? m.colors.primary : m.colors.surfaceAlt,
                                borderColor: active ? m.colors.primary : m.colors.border,
                            }]}
                        >
                            <Icon name={f.icon || 'folder-outline'} size={14} color={active ? '#FFFFFF' : m.colors.heading} />
                            <Text style={[styles.chipText, {color: active ? '#FFFFFF' : m.colors.heading}]} numberOfLines={1}>{f.label}</Text>
                        </TouchableOpacity>
                    );
                })}
            </ScrollView>

            <SectionTitle label={t('mail_organize_labels')} />
            {labelList.length === 0 ? (
                <Text style={[styles.empty, {color: m.colors.muted}]}>{t('mail_organize_no_labels')}</Text>
            ) : (
                <View style={styles.wrap}>
                    {labelList.map((l) => {
                        const on = wanted.has(l.id);
                        const tint = l.color || m.colors.info;

                        return (
                            <TouchableOpacity
                                key={l.id}
                                onPress={() => toggleLabel(l.id)}
                                activeOpacity={0.85}
                                style={[styles.chip, {
                                    backgroundColor: on ? tint + (m.isDark ? '33' : '22') : m.colors.surfaceAlt,
                                    borderColor: on ? tint : m.colors.border,
                                }]}
                            >
                                <Icon name={on ? 'check-circle' : 'tag-outline'} size={14} color={on ? tint : m.colors.muted} />
                                <Text style={[styles.chipText, {color: m.colors.heading}]} numberOfLines={1}>{l.name}</Text>
                            </TouchableOpacity>
                        );
                    })}
                </View>
            )}

            <SectionTitle label={t('mail_organize_color')} />
            <View style={styles.wrap}>
                <TouchableOpacity
                    onPress={() => setColor(null)}
                    activeOpacity={0.85}
                    accessibilityLabel={t('mail_organize_color_none')}
                    style={[styles.swatch, {borderColor: shownColor === '' || shownColor === null ? m.colors.primary : m.colors.border, backgroundColor: m.colors.surfaceAlt}]}
                >
                    <Icon name="close" size={16} color={m.colors.muted} />
                </TouchableOpacity>
                {MAIL_COLOR_IDS.map((id) => {
                    const hex = colorHex(id, m.isDark);
                    const active = shownColor === id;

                    return (
                        <TouchableOpacity
                            key={id}
                            onPress={() => setColor(id)}
                            activeOpacity={0.85}
                            accessibilityLabel={t(`mail_color_${id}`)}
                            style={[styles.swatch, {backgroundColor: hex, borderColor: active ? m.colors.heading : 'transparent'}]}
                        >
                            {active && <Icon name="check" size={16} color="#FFFFFF" />}
                        </TouchableOpacity>
                    );
                })}
            </View>

            <TouchableOpacity
                onPress={apply}
                disabled={!payload || busy}
                activeOpacity={0.85}
                style={[styles.apply, {backgroundColor: m.colors.primary, opacity: !payload || busy ? 0.45 : 1}]}
            >
                {busy ? <ActivityIndicator size="small" color="#FFFFFF" /> : <Icon name="check" size={18} color="#FFFFFF" />}
                <Text style={styles.applyText}>{t('mail_organize_apply')}</Text>
            </TouchableOpacity>
        </PremiumSheet>
    );
};

const styles = StyleSheet.create({
    sectionTitle: {fontSize: 11, fontWeight: '800', letterSpacing: 0.6, textTransform: 'uppercase', marginTop: 14, marginBottom: 8},
    rowWrap: {gap: 8, paddingRight: 8},
    wrap: {flexDirection: 'row', flexWrap: 'wrap', gap: 8},
    chip: {flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, borderWidth: 1, maxWidth: 220},
    chipText: {fontSize: 13, fontWeight: '700'},
    empty: {fontSize: 13},
    swatch: {width: 34, height: 34, borderRadius: 17, borderWidth: 2, alignItems: 'center', justifyContent: 'center'},
    apply: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, height: 50, borderRadius: 999, marginTop: 20},
    applyText: {color: '#FFFFFF', fontSize: 15, fontWeight: '800'},
});

export default MailOrganizeSheet;
